/**
 * Per-clinician week against target (5.1), in points and nothing else. The row
 * reads the same `selectWeekPoints` My week's header and the heat-map hover
 * read, so the three cannot disagree about a clinician's week.
 *
 * Unsorted on purpose: alphabetical within discipline, no comparative chips, no
 * status labels, no hours. Leadership can drill in; the screen does not rank
 * clinicians by points and does not tell anyone who is behind.
 */
import { useMemo, useState } from "react";
import { Box, Paper, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { addDaysToKey, dateKeyStart, formatInBranchZone, weekStartKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { selectUtilizationStrip, type UtilizationRow } from "../../engine/utilization";
import { useDemoState } from "../../store/useDemoStore";

const EMPLOYMENT_LABEL: Record<UtilizationRow["employment"], string> = { FT: "full-time", PT: "part-time", perDiem: "external" };

export function UtilizationStrip() {
  const state = useDemoState();
  const tz = state.config.branch.timeZone;
  const thisWeek = weekStartKey(state.meta.demoToday);
  const [weekOffset, setWeekOffset] = useState<0 | 1>(0);
  const weekStart = addDaysToKey(thisWeek, weekOffset * 7);
  const rows = useMemo(() => selectUtilizationStrip(state, weekStart), [state, weekStart]);
  const weekLabel = `${formatInBranchZone(dateKeyStart(weekStart, tz), tz, "MMM d")} – ${formatInBranchZone(dateKeyStart(addDaysToKey(weekStart, 6), tz), tz, "MMM d")}`;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Week against target
        </Typography>
        <ToggleButtonGroup size="small" exclusive value={weekOffset} onChange={(_, v: 0 | 1 | null) => v !== null && setWeekOffset(v)}>
          <ToggleButton value={0}>This week</ToggleButton>
          <ToggleButton value={1}>Next week</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary">
          {weekLabel}
        </Typography>
        {/* Just the unit. The caption used to add *placeholder values until the
            branch's table lands*, which read on screen as a loading error — the
            word "table" collides with the table you are looking at — and said
            unprompted what `demo-script.md` (Notes, Point values) says to say
            **only if asked**. The disclosure still matters: about 0.9% of the points on
            this screen come from a value Colin has confirmed, the rest from
            ours. It belongs in the talk track, where the script already has it. */}
        <Typography variant="caption" color="text.secondary">
          points
        </Typography>
      </Stack>

      <Box sx={{ overflowX: "auto" }}>
        <Box component="table" sx={{ borderCollapse: "collapse", width: "100%", minWidth: 700, "& th, & td": { borderBottom: "1px solid #eee", px: 1, py: 0.5, fontSize: 12, textAlign: "left", verticalAlign: "middle" }, "& th": { bgcolor: "#f3f3f3", fontSize: 11.5, fontWeight: 600 } }}>
          <thead>
            <tr>
              <th style={{ width: 170 }}>Clinician</th>
              <th style={{ width: 150 }}>Discipline</th>
              <th>Points against target</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.handle} data-handle={r.handle}>
                <td>
                  <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 500 }}>
                    {r.displayName}
                  </Typography>
                </td>
                <td>
                  <Typography component="span" sx={{ fontSize: 12 }}>
                    {r.discipline}
                  </Typography>
                  <Typography component="span" sx={{ fontSize: 11, color: "text.secondary", ml: 0.5 }}>
                    {EMPLOYMENT_LABEL[r.employment]}
                  </Typography>
                </td>
                <td>
                  <PointsMeter row={r} />
                </td>
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>
    </Paper>
  );
}

/** Firm solid, provisional hatched, the target tick and the ceiling behind them — the heat map's own layers, one clinician wide. */
function PointsMeter({ row }: { row: UtilizationRow }) {
  const { points, provisionalPoints, targetPoints, ceilingPoints } = row;
  const scale = Math.max(ceilingPoints, points + provisionalPoints, targetPoints, 1);
  const pct = (n: number) => `${Math.min(100, (n / scale) * 100)}%`;
  const over = points + provisionalPoints > ceilingPoints;
  return (
    <Tooltip title={`${points} points · ${provisionalPoints} reserved · target ${targetPoints} · ceiling ${ceilingPoints}`}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ position: "relative", flex: 1, height: 12, bgcolor: "#f1f1f1", borderRadius: 1, overflow: "hidden" }}>
          <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: pct(points), bgcolor: over ? "#c0504d" : "#7aa6d0" }} />
          <Box sx={{ position: "absolute", left: pct(points), top: 0, bottom: 0, width: pct(provisionalPoints), backgroundImage: "repeating-linear-gradient(135deg, #7aa6d0 0 3px, #dbe7f3 3px 6px)" }} />
          <Box sx={{ position: "absolute", left: pct(targetPoints), top: -2, bottom: -2, width: 2, bgcolor: "#333" }} title="target" />
          <Box sx={{ position: "absolute", left: pct(ceilingPoints), top: -2, bottom: -2, width: 0, borderLeft: "2px dashed #333" }} title="ceiling" />
        </Box>
        <Typography component="span" sx={{ fontSize: 11.5, minWidth: 210, whiteSpace: "nowrap" }}>
          {points} points · {provisionalPoints} reserved · target {targetPoints}
        </Typography>
      </Stack>
    </Tooltip>
  );
}
